/**
 * Human-readable rendering of a record's extraction provenance (`rawPayload`).
 * The payload shape varies per extractor (Excel: sheet/cell/month/siteCode;
 * PDF: page/region; PPT: slide), so each known key renders independently and
 * unknown payloads simply yield null (the raw JSON stays available in the
 * collapsible accordion).
 */

/** One labelled provenance part, in display order. */
const PART_BUILDERS: Array<{ key: string; render: (value: string) => string }> = [
  { key: 'sheet', render: (value) => `Sheet "${value}"` },
  { key: 'cell', render: (value) => `Cell ${value}` },
  { key: 'month', render: (value) => capitalise(value) },
  { key: 'siteCode', render: (value) => `Site ${value}` },
  { key: 'internalId', render: (value) => `Internal id ${value}` },
  { key: 'page', render: (value) => `Page ${value}` },
  { key: 'slide', render: (value) => `Slide ${value}` }
];

function capitalise(value: string): string {
  return value.length === 0 ? value : value[0].toUpperCase() + value.slice(1);
}

/** Reads a payload entry as display text, accepting strings and numbers only. */
function readPart(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  if (typeof value === 'string' && value.trim() !== '') {
    return value.trim();
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return null;
}

/**
 * A locator whose whole payload is the page or slide the record already reports
 * explicitly (`Report.pdf!p.12`, `deck.pptx!slide 3`) — as opposed to one that
 * also names an image and a position within it
 * (`deck.pptx (slide 2, image18.png)!image:2024-Jan bar`), which nothing else says.
 */
function restatesPageOrSlide(cell: string): boolean {
  const locator = cell.slice(cell.lastIndexOf('!') + 1).trim();
  return /^(p\.?\s*\d+|PDF|slide(\s+\d+)?)$/i.test(locator);
}

/**
 * Format a record's source location as a single human-readable line, e.g.
 * `Sheet "Environmental AECS" · Cell D5 · Jan · Site AECS`.
 * @param {Record<string, unknown>} rawPayload The record's original extraction payload.
 * @returns {string | null} The formatted line, or null when no known location keys exist.
 */
export function formatProvenance(rawPayload: Record<string, unknown>): string | null {
  // Rendering a document record's locator next to its typed page would read
  // "Cell Report.pdf!p.12 · Page 12" — but only drop the locator when it truly
  // says nothing more, or a chart value loses which chart and which bar it came from.
  const cell = readPart(rawPayload, 'cell');
  const hasPageOrSlide =
    readPart(rawPayload, 'page') !== null || readPart(rawPayload, 'slide') !== null;
  const dropCell = cell !== null && hasPageOrSlide && restatesPageOrSlide(cell);
  const parts = PART_BUILDERS.filter(({ key }) => !(key === 'cell' && dropCell))
    .map(({ key, render }) => {
      const part = readPart(rawPayload, key);
      return part === null ? null : render(part);
    })
    .filter((part): part is string => part !== null);
  return parts.length === 0 ? null : parts.join(' · ');
}
